import { bindServices, getPerPaged } from '@/services/api';
import { getById } from '@/services/global';
import { toPaginate, getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';
import { merge } from 'lodash';

import { subSequenceFiltersConfig } from '../utils';

const api = bindServices('subSequence');
const { ManageSubSequence } = CoreQueries;
const { BulkActionType } = CoreEnums;

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const Model = {
  namespace: 'subSequence',

  state: {
    list: [],
    orderBy: '',
    pagination: { ...meta },
    searchTable: '',
    selectedItem: {},
    openCreateOrEdit: false,
    detail: false,
    filtersConfig: subSequenceFiltersConfig,
  },

  effects: {
    *fetchList({ payload }, { call, put }) {
      try {
        const { readPriority, ...restPayload } = payload || {};
        const params = { route: 'sequenceItem', ...restPayload, ...ManageSubSequence.paged };
        const response = yield call(getPerPaged, params, {
          readPriority: readPriority ?? false,
        });
        yield put({
          type: 'queryList',
          payload: {
            ...response,
            page: payload.page,
            limit: payload.limit,
            searchTable: payload.search,
          },
        });
      } catch (error) {}
    },
    *setOrderBy({ payload }, { put }) {
      yield put({
        type: 'orderBy',
        payload,
      });
    },
    *hideAllForms({ payload = 'hideForms' }, { put }) {
      yield put({ type: payload });
    },
    *deleteItem({ id, params }, { call, put }) {
      try {
        const paramsRemove = { route: 'sequenceItem', id };
        const response = yield call(api.remove, paramsRemove);
        yield put({
          type: 'queryDeleteItem',
          payload: response,
        });

        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
        yield put({
          type: 'fetchList',
          payload: params,
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },
    *toggleCreateOrEditDrawer({ payload: { id, state, item, detail } }, { put }) {
      yield put({
        type: 'queryToggleCreateOrEditDrawer',
        payload: {
          id,
          state,
          item,
          detail: !!detail,
        },
      });
    },
    *saveItem({ payload }, { call, put }) {
      try {
        const params = { route: 'sequenceItem', data: payload.data };
        if (payload.data.id) {
          yield call(api.update, params);
        } else {
          yield call(api.create, params);
        }
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
        yield put({
          type: 'fetchList',
          payload: payload.params,
        });
        yield put({
          type: 'toggleCreateOrEditDrawer',
          payload: {
            id: null,
            state: false,
            item: null,
          },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
              autoHide: true,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },
    *getItem({ payload }, { call, put }) {
      try {
        const paramsGet = {
          route: 'sequenceItem',
          id: payload.id,
          params: { ...ManageSubSequence.detail },
        };
        const response = yield call(getById, paramsGet);
        yield put({
          type: 'queryGetItem',
          payload: {
            id: payload.id,
            item: response,
          },
        });
      } catch (error) {}
    },
    *bulkDeleteSubSequences({ payload }, { call, put, select }) {
      try {
        const { ids, idSequence } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: 'sequenceItem',
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const globalState = yield select((state) => state.global);
        const subSeqState = yield select((state) => state.subSequence);
        const baseFilter = `sequenceId eq ${idSequence}`;
        const extraFilters = getFiltersGlobalFilters(globalState.globalFilters, 'subSequence');
        const filter = extraFilters ? `${baseFilter} and ${extraFilters}` : baseFilter;

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'fetchList',
          payload: {
            page: 1,
            filter,
            search: subSeqState.searchTable || null,
            orderBy: subSeqState.orderBy,
            readPriority: true,
          },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },
  },

  reducers: {
    queryList(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: merge(state.pagination, toPaginate(payload)),
        searchTable: payload.searchTable ? payload.searchTable : '',
      };
    },
    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },
    hideForms(state) {
      return {
        ...state,
        orderBy: '',
      };
    },
    queryToggleCreateOrEditDrawer(state, { payload }) {
      return {
        ...state,
        id: payload.id,
        openCreateOrEdit: payload.state,
        selectedItem: payload.item,
        detail: payload.detail ?? false,
      };
    },
    queryGetItem(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.item,
      };
    },
    setPatternInSelectedItem(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.itemValues,
      };
    },
    setSearchTable(state, { payload }) {
      return {
        ...state,
        searchTable: payload ?? '',
      };
    },
    clearList(state) {
      return {
        ...state,
        list: [],
        pagination: { ...meta },
      };
    },
  },
};

export default Model;
